import { FlexProps } from '@chakra-ui/react';
import { Tooltip } from '../ui/tooltip';
import { ESignRecipient } from '@monetize/types/app';
import {
  MdCheck,
  MdCheckCircle,
  MdEmail,
  MdLens,
  MdRemoveCircle,
} from 'react-icons/md';
import {
  MBox,
  MFlex,
  MIcon,
  MSkeleton,
  MText,
  MTooltip,
  MVStack,
} from '../../components/Monetize';

import { toDateTimeShort } from '@monetize/utils/core';
import { parseISO } from 'date-fns/parseISO';
import { IconType } from 'react-icons';
function getIcon(
  isComplete: boolean,
  isFinalStep: boolean,
): { icon: IconType; color: string } {
  if (isComplete && isFinalStep) {
    return {
      icon: MdCheckCircle,
      color: 'tGreen.approval',
    };
  }
  if (isComplete) {
    return {
      icon: MdCheck,
      color: 'tGreen.approval',
    };
  }
  return {
    icon: MdLens,
    color: 'tGray.back',
  };
}

function getActionLabels(
  recipient: ESignRecipient,
  isCC: boolean,
  timeZoneLabel?: string,
) {
  const output: {
    label: string;
    tooltip?: string;
    icon: IconType;
    color: string;
  }[] = [];

  output.push({
    label: !recipient.sentDateTime
      ? 'Not sent'
      : `Sent at ${toDateTimeShort(parseISO(recipient.sentDateTime), 'userTimezone')}`,
    tooltip:
      recipient.sentDateTime && timeZoneLabel
        ? `Time zone: ${timeZoneLabel}`
        : undefined,
    ...getIcon(!!recipient.sentDateTime, false),
  });

  output.push({
    label: !recipient.deliveredDateTime
      ? 'Not opened'
      : `Opened at ${toDateTimeShort(parseISO(recipient.deliveredDateTime), 'userTimezone')}`,
    tooltip:
      recipient.deliveredDateTime && timeZoneLabel
        ? `Time zone: ${timeZoneLabel}`
        : undefined,
    ...getIcon(!!recipient.deliveredDateTime, false),
  });

  if (!isCC) {
    output.push({
      label: !recipient.signedDateTime
        ? 'Not signed'
        : `Signed at ${toDateTimeShort(parseISO(recipient.signedDateTime), 'userTimezone')}`,
      tooltip:
        recipient.signedDateTime && timeZoneLabel
          ? `Time zone: ${timeZoneLabel}`
          : undefined,
      ...getIcon(!!recipient.signedDateTime, true),
    });
  }

  if (!isCC && recipient.declinedDateTime) {
    output.push({
      label: `Declined at ${toDateTimeShort(parseISO(recipient.declinedDateTime), 'userTimezone')}`,
      tooltip: timeZoneLabel ? `Time zone: ${timeZoneLabel}` : undefined,
      icon: MdRemoveCircle,
      color: 'tRed.base',
    });
  }
  return output;
}
export const ESignRecipientItem = ({
  recipient,
  isCC = false,
  timeZoneLabel,
  isLoading = false,
  ...rest
}: {
  recipient?: ESignRecipient;
  isCC?: boolean;
  timeZoneLabel?: string;
  isLoading?: boolean;
} & FlexProps) => {
  if (isLoading) {
    return (
      <MVStack gap="2">
        <MSkeleton height="20px" w="100%" />
        <MSkeleton height="20px" w="100%" />
        <MSkeleton height="20px" w="100%" />
      </MVStack>
    );
  }
  if (!recipient) {
    return null;
  }

  return (
    <MFlex position="relative" flexDirection="column" mt={1} ml={1} {...rest}>
      <MFlex>
        <MText fontWeight="600" lineClamp={2}>
          {recipient.name}
        </MText>
        <MTooltip label={recipient.email} placement="top-start">
          <MBox>
            <MIcon as={MdEmail} ml={1} />
          </MBox>
        </MTooltip>
      </MFlex>
      {getActionLabels(recipient, isCC, timeZoneLabel).map(
        ({ label, tooltip, color, icon }) => (
          <MFlex key={label}>
            <MIcon as={icon} color={color} mt={0.5} mr={1} />
            <Tooltip
              content={tooltip}
              openDelay={500}
              positioning={{
                placement: 'right',
              }}
            >
              <MText>{label}</MText>
            </Tooltip>
          </MFlex>
        ),
      )}
    </MFlex>
  );
};
